import { PaymentProcessorEnum, Tooltip, getPaymentProcessorTitle, toast } from '@systemeio/ui'
import { Integration } from 'modules/profile/payment-gateways/components/integration'
import { useConnectedPaymentIntegrations } from 'modules/profile/payment-gateways/hooks/use-connected-payment-integrations'
import PaypalRestConnectButton from 'modules/profile/payment-gateways/integrations/paypal-rest/components/paypal-rest-connect-button'
import { PaypalRestMessages } from 'modules/profile/payment-gateways/integrations/paypal-rest/components/paypal-rest-messages'
import { usePaypalRestApi } from 'modules/profile/payment-gateways/integrations/paypal-rest/hooks'
import { PaypalRestPaymentIntegrationInterface } from 'modules/profile/payment-gateways/integrations/paypal-rest/types'
import { getPaypalHelpPageLink } from 'modules/profile/payment-gateways/integrations/paypal-soap/utils'
import React, { FC, useState } from 'react'
import ConfirmationModal from 'shared/components/confirmation-modal'
import TransLoco from 'shared/components/trans-loco'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { BinIcon, PaypalLogoIcon } from '@systemeio/ui'

export const PaypalRest: FC = () => {
  const { user } = useUser()
  const { t } = useLocoTranslation()
  const { getConnectedPaymentIntegration, removePaymentIntegration } =
    useConnectedPaymentIntegrations()

  const [deleteOpened, setDeleteOpened] = useState(false)

  const title = getPaymentProcessorTitle(PaymentProcessorEnum.PaypalRest)

  const paypalRestIntegration =
    getConnectedPaymentIntegration<PaypalRestPaymentIntegrationInterface>({
      processor: PaymentProcessorEnum.PaypalRest,
    })

  const { disconnect } = usePaypalRestApi()

  const onDelete = async () => {
    await disconnect()
    await removePaymentIntegration<PaypalRestPaymentIntegrationInterface>({
      processor: PaymentProcessorEnum.PaypalRest,
    })
    toast.success(
      t('dashboard.settings.payment-gateways.account_removed', {
        payment: title,
      }),
    )
  }

  return (
    <Integration
      logo={<PaypalLogoIcon />}
      additionalInfo={
        <a
          className="primary-link"
          href={getPaypalHelpPageLink(user?.dashboardLocale)}
          target="_blank"
          rel="noreferrer"
        >
          <TransLoco
            t={t}
            i18nKey="dashboard.settings.payment_gateways.help_article_label"
            values={{ integration: title }}
          />
        </a>
      }
    >
      {paypalRestIntegration ? (
        <div className={'flex w-full justify-between gap-3 overflow-hidden'}>
          <div className={'flex flex-col gap-1 truncate'}>
            {paypalRestIntegration.details && paypalRestIntegration.details.merchantId !== null && (
              <p className={'flex items-center gap-1 text-base lg:text-lg'}>
                <span>{`${t('dashboard.settings.payment-gateways.merchant_id')}:`}</span>
                <span className={'text-text-primary truncate'}>
                  {paypalRestIntegration.details.merchantId}
                </span>
              </p>
            )}
            {!paypalRestIntegration.readyToUse && (
              <>
                <p className={'text-yellow'}>
                  {t('dashboard.settings.payment-gateways.integration_in_progress')}
                </p>
                <PaypalRestMessages />
              </>
            )}
          </div>
          <Tooltip
            mode={'hover'}
            label={t('dashboard.settings.payment-gateways.remove_integration', {
              payment: title,
            })}
            wrapperClassName={'flex items-center'}
            className={'h-fit'}
          >
            <button className={'group outline-hidden'} onClick={() => setDeleteOpened(true)}>
              <BinIcon
                className={
                  'group-focus-visible:fill-danger hover:fill-danger main-transition-colors h-[16px] w-[16px]'
                }
              />
            </button>
          </Tooltip>
          <ConfirmationModal
            title={t('dashboard.settings.payment-gateways.remove_integration_description', {
              payment: title,
            })}
            confirmationText={t('dashboard.settings.payment-gateways.remove_payment_text.paypal')}
            opened={deleteOpened}
            onClose={() => setDeleteOpened(false)}
            onConfirm={onDelete}
          />
        </div>
      ) : (
        <PaypalRestConnectButton />
      )}
    </Integration>
  )
}
